import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const Tag = ({ light }) => (
  <span className={`inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.16em] uppercase px-3 py-1.5 rounded-full backdrop-blur-md ${light ? "bg-black/70 text-[#F5F3EE]" : "bg-black/55 text-[#D4AF5A] border border-[#D4AF5A]/30"}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF5A]" /> Projeto conceitual
  </span>
);

const Cta = ({ onClick, id, dark }) => (
  <button onClick={onClick} data-testid={`project-cta-${id}`} className={`mt-8 group inline-flex items-center gap-3 h-12 px-6 rounded-full text-[13px] font-medium tracking-[0.06em] transition-[background-color,color,border-color] duration-300 ${dark ? "bg-[#050505] text-[#F5F3EE] hover:bg-[#1a1a1a]" : "border border-[#D4AF5A]/40 text-[#D4AF5A] hover:bg-[#D4AF5A] hover:text-[#050505]"}`}>
    Quero um site como este <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-500" />
  </button>
);

const Parallax = ({ src, alt, className, imgClass = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img style={{ y }} src={src} alt={alt} loading="lazy" decoding="async" width="1400" height="933" className={`absolute inset-0 w-full h-[116%] -top-[8%] object-cover ${imgClass}`} />
    </div>
  );
};

const Meta = ({ n, cat }) => (
  <div className="flex items-center gap-3 mb-5"><span className="font-mono text-xs text-gold">{n}</span><span className="label">{cat}</span></div>
);

export const Portfolio = ({ onBrief }) => (
  <section id="projetos" data-testid="portfolio-section" className="py-24 lg:py-40">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <SectionHead index="02" label="Seleção de projetos" title={<>Estudos de direção <span className="font-serif italic font-normal text-gold">visual</span>.</>}>
        Projetos conceituais criados para demonstrar como pensamos cada segmento. Não representam clientes reais.
      </SectionHead>

      <article data-testid="project-marcenaria" className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
        <Reveal className="lg:col-span-8 relative">
          <Parallax src="/img/marcenaria.webp" alt="Cozinha planejada em madeira escura — projeto conceitual de marcenaria" className="aspect-[4/5] sm:aspect-[16/11] rounded-[4px]" imgClass="brightness-[.8] contrast-[1.05]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,transparent_30%,rgba(5,5,5,.75))] pointer-events-none" />
          <div className="absolute top-4 left-4"><Tag /></div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-4 lg:pb-8">
          <Meta n="2.1" cat="Marcenaria de alto padrão" />
          <h3 className="font-display text-4xl sm:text-5xl font-bold tracking-[-0.03em] uppercase">Aura Madeiras</h3>
          <p className="mt-5 text-[#A7A7AD] leading-relaxed">Mobiliário sob medida com refinamento artesanal e presença digital marcante. Fotografia em penumbra e tipografia sóbria para valorizar o material.</p>
          <Cta onClick={onBrief} id="marcenaria" />
        </Reveal>
      </article>
    </div>

    <article data-testid="project-arquitetura" className="mt-24 lg:mt-40 bg-[#F5F3EE] text-[#050505]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <Reveal className="lg:col-span-5 lg:order-2 lg:col-start-8">
          <div className="flex items-center gap-3 mb-5"><span className="font-mono text-xs text-[#8a6d2c]">2.2</span><span className="font-mono text-xs tracking-[0.18em] uppercase text-[#55524b]">Arquitetura & interiores</span></div>
          <h3 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-[-0.02em]">Atelier <em>Linha</em></h3>
          <p className="mt-6 text-[#45423c] leading-relaxed max-w-md">Um portfólio que respira: muito espaço em branco, imagens em grande escala e navegação silenciosa — o projeto arquitetônico é o protagonista.</p>
          <Cta onClick={onBrief} id="arquitetura" dark />
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 lg:order-1 relative">
          <Parallax src="/img/arquitetura.webp" alt="Escada minimalista em concreto claro — projeto conceitual de arquitetura" className="aspect-[3/4] lg:aspect-[4/5] rounded-t-[999px]" />
          <div className="absolute top-6 left-1/2 -translate-x-1/2"><Tag light /></div>
        </Reveal>
      </div>
    </article>

    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 mt-24 lg:mt-40 grid lg:grid-cols-12 gap-16 lg:gap-12">
      <article data-testid="project-loja" className="lg:col-span-7">
        <Reveal className="relative">
          <Parallax src="/img/loja.webp" alt="Interior de loja com prateleiras iluminadas — projeto conceitual de varejo" className="aspect-[16/10] [clip-path:polygon(0_0,100%_0,100%_86%,88%_100%,0_100%)]" imgClass="sepia-[.25] brightness-[.85]" />
          <div className="absolute top-4 left-4"><Tag /></div>
        </Reveal>
        <Reveal delay={0.1} className="mt-8 grid sm:grid-cols-[1fr_auto] gap-6 items-end">
          <div>
            <Meta n="2.3" cat="Marcas & lojas" />
            <h3 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em]">Maison Ocre</h3>
            <p className="mt-4 text-[#A7A7AD] leading-relaxed max-w-md">Vitrine editorial para uma marca de design de interiores: produtos em destaque, coleções e contato direto pelo WhatsApp.</p>
          </div>
          <Cta onClick={onBrief} id="loja" />
        </Reveal>
      </article>

      <article data-testid="project-clinica" className="lg:col-span-5 lg:pt-48">
        <Reveal className="bg-[#111111] p-6 sm:p-8 rounded-[4px] relative">
          <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#D4AF5A]/60 to-transparent" />
          <Meta n="2.4" cat="Clínicas & serviços" />
          <h3 className="font-display text-[2.6rem] sm:text-6xl font-semibold leading-[0.9] tracking-[-0.04em]">Clínica<br /><span className="font-serif italic font-normal text-gold">Vértice</span></h3>
          <div className="relative mt-8">
            <Parallax src="/img/clinica.webp" alt="Recepção clínica com cadeiras pretas — projeto conceitual de clínica" className="aspect-[4/3] rounded-[4px]" imgClass="grayscale-[.4]" />
            <div className="absolute bottom-3 left-3"><Tag /></div>
          </div>
          <p className="mt-6 text-[#A7A7AD] leading-relaxed">Clareza e acolhimento: especialidades bem explicadas, agendamento facilitado e uma estética que transmite cuidado.</p>
          <Cta onClick={onBrief} id="clinica" />
        </Reveal>
      </article>
    </div>
  </section>
);
